/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dropdown, Flex, Modal } from "antd";
import {
  useDeleteCategoryMutation,
  useGetCategoriesQuery,
} from "../../../../app/features/admin/admin.api";
import type { Category } from "../../../../app/features/admin/admin.types";
import ActionIcon from "../../../../assets/icons/ActionIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import CustomTable from "../../../../components/CustomTable";
import CustomTitle from "../../../../components/CustomTitle";
import StatusTag from "../../../../components/StatusTag";
import useFilters from "../../../../hooks/useFilters";
import useModal from "../../../../hooks/useModal";
import DashboardLayout from "../../../../layouts/DashboardLayout";
import { toast } from "../../../../lib/toast";
import type { ColumnsType } from "../../../../types/global.types";
import { countParPageSerialNumber } from "../../../../utils/global.utils";
import CategoryDetails from "./_components/CategoryDetails";
import CategoryModal from "./_components/CategoryModal";

const Categories = () => {
  const {
    page,
    limit,
    handleLimit,
    // handleSearch,
    // handleFilter,
    handlePagination,
  } = useFilters<{
    status?: string | undefined;
    search?: string | undefined;
  }>();
  const { open, handleOpen, handleClose } = useModal<Category>();
  const { data, isLoading } = useGetCategoriesQuery({});

  const [deleteCategory] = useDeleteCategoryMutation();

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this category?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCategory({ id }).unwrap();
          toast("success", "Category deleted successfully");
        } catch (error: any) {
          toast("error", error?.data?.message || "Failed to delete category");
        }
      },
    });
  };

  const statusOptions = [
    { label: "Active", value: "true", className: "status-success" },
    { label: "Inactive", value: "false", className: "status-error" },
  ];

  const columns: ColumnsType<Category> = [
    {
      key: "sl",
      title: "#SL",
      align: "center",
      width: 60,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    // {
    //   key: "slug",
    //   title: "Slug",
    //   dataIndex: "slug",
    // },
    {
      key: "sortOrder",
      title: "Sort Order",
      dataIndex: "sortOrder",
      align: "center",
    },
    {
      key: "isActive",
      title: "Status",
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <StatusTag value={isActive ? "true" : "false"} config={statusOptions} />
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "end",
      width: 100,
      render: (_: any, record: Category) => (
        <Dropdown
          overlayClassName="w-36"
          placement="bottomRight"
          menu={{
            items: [
              {
                key: "view",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex justify="center" className="w-6 text-theme-sky-500">
                      <ViewIcon size={24} />
                    </Flex>
                    <span className="text-sm font-medium">View</span>
                  </Flex>
                ),
                onClick: () => handleOpen({ type: "VIEW", data: record }),
              },
              {
                key: "edit",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex
                      justify="center"
                      className="w-6 text-theme-yellow-500"
                    >
                      <EditIcon size={18} />
                    </Flex>
                    <span className="text-sm font-medium">Edit</span>
                  </Flex>
                ),
                onClick: () => handleOpen({ type: "UPDATE", data: record }),
              },
              {
                key: "delete",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex justify="center" className="w-6 text-theme-error-500">
                      <DeleteIcon size={16} />
                    </Flex>
                    <span className="text-sm font-medium">Delete</span>
                  </Flex>
                ),
                onClick: () => handleDelete(record?.id),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            size="small"
            icon={<ActionIcon />}
            classNames={{ icon: "!size-8 flex items-center justify-center" }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <DashboardLayout title="Categories">
      <CustomTitle
        title="Category Management"
        description="Manage all course categories"
      >
        <Button
          onClick={() => handleOpen({ type: "ADD" })}
          className="btn-primary"
        >
          <AddIcon /> Add New Category
        </Button>
      </CustomTitle>

      <CustomTable
        total={10}
        page={page}
        limit={limit}
        loading={isLoading}
        columns={columns}
        dataSource={Array.isArray(data) ? data : []}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        tableTitle="All Categories"
        hideRowSelect
        // filterSection={
        //   <Flex align="center" gap={8}>
        //     <Input
        //       size="middle"
        //       placeholder="Search category"
        //       prefix={<SearchIcon size={14} />}
        //       className="max-w-[200px] input-bordered"
        //       onChange={handleSearch}
        //     />
        //     <Select
        //       allowClear
        //       size="middle"
        //       placeholder="Status"
        //       style={{ width: 120 }}
        //       className="input-bordered"
        //       options={[
        //         { label: "Active", value: "active" },
        //         { label: "Inactive", value: "inactive" },
        //       ]}
        //       onChange={(val) => handleFilter("status", val)}
        //     />
        //   </Flex>
        // }
      />

      {(open?.type === "ADD" || open?.type === "UPDATE") && (
        <CategoryModal
          open={open?.open}
          onCancel={handleClose}
          initialValues={open?.data}
          type={open?.type}
        />
      )}

      {open?.type === "VIEW" && (
        <CategoryDetails
          open={open?.open}
          onCancel={handleClose}
          category={open?.data}
          type={open?.type}
        />
      )}
    </DashboardLayout>
  );
};

export default Categories;
